import React from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill, type StatusVariant } from '../patterns';
import { formatQty, formatPct, selectDisplayUnit, convertToUnit } from '../../utils/chemicalStockUsageMath';
import type { EnrichedChemicalStockUsageRow, VarianceStatus } from '../../utils/chemicalStockUsageMath';

export interface PlannedVsActualMeterProps {
  row: EnrichedChemicalStockUsageRow;
}

const VARIANCE_VARIANT: Record<VarianceStatus, StatusVariant> = {
  ontarget:   'ok',
  overspend:  'danger',
  underspend: 'info',
};

const VARIANCE_LABEL_KEY: Record<VarianceStatus, string> = {
  ontarget:   'spray.dashboard.chemicalStockUsage.status.ontarget',
  overspend:  'spray.dashboard.chemicalStockUsage.status.overspend',
  underspend: 'spray.dashboard.chemicalStockUsage.status.underspend',
};

const VARIANCE_CAP = 0.5;

export const PlannedVsActualMeter: React.FC<PlannedVsActualMeterProps> = ({ row }) => {
  const { t } = useTranslation();

  if (row.cPlanned === 0) {
    return (
      <p className="csu-meter-empty">
        {t('spray.dashboard.chemicalStockUsage.empty.noCompletedSessions')}
      </p>
    );
  }

  // Pick one display unit for both Planned and Actual based on whichever is
  // larger. Sub-1 L → ml; sub-1 kg → g; everything else keeps its source unit.
  const dUnit     = selectDisplayUnit([row.cPlanned, row.cActual], row.unit);
  const dPlanned  = convertToUnit(row.cPlanned, row.unit, dUnit);
  const dActual   = convertToUnit(row.cActual,  row.unit, dUnit);

  const dev   = Math.max(-VARIANCE_CAP, Math.min(VARIANCE_CAP, row.variancePct));
  const left  = dev >= 0 ? '50%' : `${50 + (dev / VARIANCE_CAP) * 50}%`;
  const width = `${Math.abs(dev / VARIANCE_CAP * 50)}%`;
  const variant   = VARIANCE_VARIANT[row.varianceStatus];
  const pillLabel = t(VARIANCE_LABEL_KEY[row.varianceStatus]);

  return (
    <div className="csu-meter csu-meter--variance">
      <div className="csu-meter-numbers">
        <div className="csu-num">
          <span className="csu-num-label">{t('spray.dashboard.chemicalStockUsage.label.planned')}</span>
          <span className="csu-num-val">
            {formatQty(dPlanned)} <span className="csu-num-unit">{dUnit}</span>
          </span>
        </div>
        <div className="csu-num">
          <span className="csu-num-label">{t('spray.dashboard.chemicalStockUsage.label.actual')}</span>
          <span className="csu-num-val">
            {formatQty(dActual)} <span className="csu-num-unit">{dUnit}</span>
          </span>
          <span className={`csu-num-sub csu-variance-pct csu-variance-pct--${row.varianceStatus}`}>
            {formatPct(row.variancePct)}
          </span>
        </div>
        <span className={`csu-pill-with-dot csu-pill-with-dot--${row.varianceStatus}`}>
          <StatusPill variant={variant}>{pillLabel}</StatusPill>
        </span>
      </div>
      <div className="csu-variance">
        <div className="csu-variance-axis">
          <span>−{Math.round(VARIANCE_CAP * 100)}%</span>
          <span>0%</span>
          <span>+{Math.round(VARIANCE_CAP * 100)}%</span>
        </div>
        <div className="csu-variance-track">
          <div className="csu-variance-tol" />
          <div className="csu-variance-zero" />
          <div className={`csu-variance-bar csu-variance-bar--${row.varianceStatus}`} style={{ left, width }} />
        </div>
      </div>
    </div>
  );
};

export default PlannedVsActualMeter;
